import type { Routemize_DTOs_AppConfiguration_AppConfigurationDto } from '../../api/models/Routemize_DTOs_AppConfiguration_AppConfigurationDto';
import { UserRoleConfigManager, type UserRoleType } from '../users/UserRoleConfig';

/**
 * ABP Permission Policy Names
 */
export const ABP_POLICIES = {
  // Setting Management
  SETTING_MANAGEMENT_EMAILING: 'SettingManagement.Emailing',
  SETTING_MANAGEMENT_EMAILING_TEST: 'SettingManagement.Emailing.Test',
  SETTING_MANAGEMENT_TIMEZONE: 'SettingManagement.TimeZone',
  
  // Identity Roles
  IDENTITY_ROLES: 'AbpIdentity.Roles',
  IDENTITY_ROLES_CREATE: 'AbpIdentity.Roles.Create',
  IDENTITY_ROLES_UPDATE: 'AbpIdentity.Roles.Update',
  IDENTITY_ROLES_DELETE: 'AbpIdentity.Roles.Delete',
  IDENTITY_ROLES_MANAGE_PERMISSIONS: 'AbpIdentity.Roles.ManagePermissions',
  
  // Identity Users
  IDENTITY_USERS: 'AbpIdentity.Users',
  IDENTITY_USERS_CREATE: 'AbpIdentity.Users.Create',
  IDENTITY_USERS_UPDATE: 'AbpIdentity.Users.Update',
  IDENTITY_USERS_UPDATE_MANAGE_ROLES: 'AbpIdentity.Users.Update.ManageRoles',
  IDENTITY_USERS_DELETE: 'AbpIdentity.Users.Delete',
  IDENTITY_USERS_MANAGE_PERMISSIONS: 'AbpIdentity.Users.ManagePermissions',
  
  // Tenant Management
  TENANT_MANAGEMENT_TENANTS: 'AbpTenantManagement.Tenants',
  TENANT_MANAGEMENT_TENANTS_CREATE: 'AbpTenantManagement.Tenants.Create',
  TENANT_MANAGEMENT_TENANTS_UPDATE: 'AbpTenantManagement.Tenants.Update',
  TENANT_MANAGEMENT_TENANTS_DELETE: 'AbpTenantManagement.Tenants.Delete',
  TENANT_MANAGEMENT_TENANTS_MANAGE_FEATURES: 'AbpTenantManagement.Tenants.ManageFeatures',
  TENANT_MANAGEMENT_TENANTS_MANAGE_CONNECTION_STRINGS: 'AbpTenantManagement.Tenants.ManageConnectionStrings',
  
  // Global Codes / Master Data
  GLOBAL_CODES: 'Routemize.GlobalCodes',
  GLOBAL_CODES_CREATE: 'Routemize.GlobalCodes.Create',
  GLOBAL_CODES_UPDATE: 'Routemize.GlobalCodes.Update',
  GLOBAL_CODES_DELETE: 'Routemize.GlobalCodes.Delete'
} as const;

export type ABPPolicy = typeof ABP_POLICIES[keyof typeof ABP_POLICIES];

/**
 * Business logic manager for authorization and permission management
 * Handles permission checking, authorization rules, and access control business logic
 */
export class PermissionManager {
  
  /**
   * Check if the current user has a specific policy
   */
  hasPolicy(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, policy: ABPPolicy): boolean {
    if (!config?.auth?.grantedPolicies) {
      return false;
    }

    return config.auth.grantedPolicies[policy] === true;
  }

  /**
   * Check if the current user has a specific permission (generic version for any permission string)
   */
  hasPermission(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, permission: string): boolean {
    if (!config?.auth?.grantedPolicies) {
      return false;
    }

    return config.auth.grantedPolicies[permission] === true;
  }

  /**
   * Check if the current user has any of the specified policies
   */
  hasAnyPolicy(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, policies: ABPPolicy[]): boolean {
    return policies.some(policy => this.hasPolicy(config, policy));
  }

  /**
   * Check if the current user has all of the specified policies
   */
  hasAllPolicies(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, policies: ABPPolicy[]): boolean {
    return policies.every(policy => this.hasPolicy(config, policy));
  }

  /**
   * Get all granted policies for the current user
   */
  getGrantedPolicies(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): Record<string, boolean> {
    return config?.auth?.grantedPolicies || {};
  }

  // Business authorization rules

  /**
   * Business rule: Check if user can access admin area (has any admin policies)
   */
  canAccessAdminArea(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const adminPolicies = [
      ABP_POLICIES.IDENTITY_USERS,
      ABP_POLICIES.IDENTITY_ROLES,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS,
      ABP_POLICIES.SETTING_MANAGEMENT_EMAILING
    ];

    if (this.hasAnyPolicy(config, adminPolicies)) return true;
    return this.hasPermission(config, 'Routemize.Partner.Portal');
  }

  canAccessPartnerPortal(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Partner.Portal');
  }

  // User Management Business Rules

  /**
   * Check if user can access user management
   */
  canAccessUserManagement(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS);
  }

  /**
   * Check if user can create users
   */
  canCreateUsers(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS_CREATE);
  }

  /**
   * Check if user can update users
   */
  canUpdateUsers(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS_UPDATE);
  }

  /**
   * Check if user can delete users
   */
  canDeleteUsers(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS_DELETE);
  }

  /**
   * Check if user can manage user roles
   */
  canManageUserRoles(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS_UPDATE_MANAGE_ROLES);
  }

  /**
   * Check if user can manage user permissions
   */
  canManageUserPermissions(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_USERS_MANAGE_PERMISSIONS);
  }

  // Tenant Management Business Rules

  /**
   * Check if user can access tenant management
   */
  canAccessTenantManagement(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS);
  }

  /**
   * Check if user can update tenants
   */
  canUpdateTenants(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_UPDATE);
  }

  /**
   * Check if user can delete tenants
   */
  canDeleteTenants(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_DELETE);
  }

  /**
   * Check if user can manage tenant features
   */
  canManageTenantFeatures(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_MANAGE_FEATURES);
  }

  /**
   * Check if user can manage tenant connection strings
   */
  canManageTenantConnectionStrings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_MANAGE_CONNECTION_STRINGS);
  }

  // Role Management Business Rules

  /**
   * Check if user can access role management
   */
  canAccessRoleManagement(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_ROLES);
  }

  /**
   * Check if user can create roles
   */
  canCreateRoles(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_ROLES_CREATE);
  }

  /**
   * Check if user can update roles
   */
  canUpdateRoles(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_ROLES_UPDATE);
  }

  /**
   * Check if user can delete roles
   */
  canDeleteRoles(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_ROLES_DELETE);
  }

  /**
   * Check if user can manage role permissions
   */
  canManageRolePermissions(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.IDENTITY_ROLES_MANAGE_PERMISSIONS);
  }

  // Feature Management Business Rules

  // Settings Management Business Rules

  /**
   * Check if user can manage email settings
   */
  canManageEmailSettings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.SETTING_MANAGEMENT_EMAILING);
  }

  /**
   * Check if user can test email settings
   */
  canTestEmailSettings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.SETTING_MANAGEMENT_EMAILING_TEST);
  }

  /**
   * Check if user can manage timezone settings
   */
  canManageTimezoneSettings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.SETTING_MANAGEMENT_TIMEZONE);
  }

  // Master Data Business Rules

  /**
   * Business rule: Check if user can manage master data (any master data operation)
   */
  canManageMasterData(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const masterDataPolicies = [
      ABP_POLICIES.GLOBAL_CODES,
      ABP_POLICIES.GLOBAL_CODES_CREATE,
      ABP_POLICIES.GLOBAL_CODES_UPDATE,
      ABP_POLICIES.GLOBAL_CODES_DELETE
    ];
    
    return this.hasAnyPolicy(config, masterDataPolicies);
  }

  /**
   * Check if user can create master data
   */
  canCreateMasterData(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.GLOBAL_CODES_CREATE);
  }

  /**
   * Check if user can update master data
   */
  canUpdateMasterData(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.GLOBAL_CODES_UPDATE);
  }

  /**
   * Check if user can delete master data
   */
  canDeleteMasterData(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPolicy(config, ABP_POLICIES.GLOBAL_CODES_DELETE);
  }

  /**
   * Check if user can impersonate other users
   */
  canImpersonateUsers(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Users.Impersonate');
  }

  // Advanced Business Rules

  /**
   * Business rule: Check if user has comprehensive admin privileges
   */
  isSuperAdmin(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const superAdminPolicies = [
      ABP_POLICIES.IDENTITY_USERS,
      ABP_POLICIES.IDENTITY_ROLES,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS,
      ABP_POLICIES.SETTING_MANAGEMENT_EMAILING
    ];
    
    return this.hasAllPolicies(config, superAdminPolicies);
  }

  /**
   * Business rule: Check if user can perform tenant administration
   */
  isTenantAdmin(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const tenantAdminPolicies = [
      ABP_POLICIES.IDENTITY_USERS,
      ABP_POLICIES.IDENTITY_ROLES,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_MANAGE_FEATURES
    ];
    
    return this.hasAllPolicies(config, tenantAdminPolicies);
  }

  /**
   * Business rule: Check if user has read-only admin access
   */
  hasReadOnlyAdminAccess(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    // User can view admin areas but cannot create/update/delete
    const viewPolicies = [
      ABP_POLICIES.IDENTITY_USERS,
      ABP_POLICIES.IDENTITY_ROLES,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS
    ];
    
    const editPolicies = [
      ABP_POLICIES.IDENTITY_USERS_CREATE,
      ABP_POLICIES.IDENTITY_USERS_UPDATE,
      ABP_POLICIES.IDENTITY_USERS_DELETE,
      ABP_POLICIES.IDENTITY_ROLES_CREATE,
      ABP_POLICIES.IDENTITY_ROLES_UPDATE,
      ABP_POLICIES.IDENTITY_ROLES_DELETE,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_CREATE,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_UPDATE,
      ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_DELETE
    ];
    
    return this.hasAnyPolicy(config, viewPolicies) && !this.hasAnyPolicy(config, editPolicies);
  }

  /**
   * Business rule: Get user's permission level for a specific domain
   */
  getPermissionLevel(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, domain: 'users' | 'roles' | 'tenants' | 'settings'): 'none' | 'read' | 'write' | 'admin' {
    const domainPolicies = {
      users: {
        read: ABP_POLICIES.IDENTITY_USERS,
        write: [ABP_POLICIES.IDENTITY_USERS_CREATE, ABP_POLICIES.IDENTITY_USERS_UPDATE],
        admin: [ABP_POLICIES.IDENTITY_USERS_DELETE, ABP_POLICIES.IDENTITY_USERS_MANAGE_PERMISSIONS]
      },
      roles: {
        read: ABP_POLICIES.IDENTITY_ROLES,
        write: [ABP_POLICIES.IDENTITY_ROLES_CREATE, ABP_POLICIES.IDENTITY_ROLES_UPDATE],
        admin: [ABP_POLICIES.IDENTITY_ROLES_DELETE, ABP_POLICIES.IDENTITY_ROLES_MANAGE_PERMISSIONS]
      },
      tenants: {
        read: ABP_POLICIES.TENANT_MANAGEMENT_TENANTS,
        write: [ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_CREATE, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_UPDATE],
        admin: [ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_DELETE, ABP_POLICIES.TENANT_MANAGEMENT_TENANTS_MANAGE_FEATURES]
      },
      settings: {
        read: ABP_POLICIES.SETTING_MANAGEMENT_EMAILING,
        write: [ABP_POLICIES.SETTING_MANAGEMENT_EMAILING],
        admin: [ABP_POLICIES.SETTING_MANAGEMENT_EMAILING_TEST]
      }
    };

    const policies = domainPolicies[domain];
    
    if (this.hasAnyPolicy(config, Array.isArray(policies.admin) ? policies.admin : [policies.admin])) {
      return 'admin';
    }
    
    if (this.hasAnyPolicy(config, Array.isArray(policies.write) ? policies.write : [policies.write])) {
      return 'write';
    }
    
    if (this.hasPolicy(config, policies.read)) {
      return 'read';
    }
    
    return 'none';
  }

  /**
   * Business rule: Check if user can access a specific admin page
   */
  canAccessAdminPage(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null, page: string): boolean {
    const pagePermissions: Record<string, ABPPolicy[]> = {
      'users': [ABP_POLICIES.IDENTITY_USERS],
      'roles': [ABP_POLICIES.IDENTITY_ROLES],
      'tenants': [ABP_POLICIES.TENANT_MANAGEMENT_TENANTS],
      'settings': [ABP_POLICIES.SETTING_MANAGEMENT_EMAILING, ABP_POLICIES.SETTING_MANAGEMENT_TIMEZONE],
      'master-data': [ABP_POLICIES.GLOBAL_CODES]
    };
    
    const requiredPolicies = pagePermissions[page];
    if (!requiredPolicies) {
      return false;
    }
    
    return this.hasAnyPolicy(config, requiredPolicies);
  }

  // ========================================
  // Booking System Permissions
  // Uses actual backend grantedPolicies strings
  // ========================================

  /**
   * Check if user can manage all bookings (not just their own)
   */
  canManageAllBookings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Check if user can view bookings (own or all depending on permissions)
   */
  canViewBookings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Read') ||
           this.hasPermission(config, 'Routemize.Appointment');
  }

  /**
   * Check if user can only view their own bookings (has read but not update)
   */
  canViewOwnBookingsOnly(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Read') &&
           !this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Check if user can manage booking requests
   */
  canManageRequests(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Requests.Update') ||
           this.hasPermission(config, 'Routemize.Requests');
  }

  /**
   * Check if user can reassign team members to bookings/appointments
   */
  canReassignUsers(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Requests.Update');
  }

  /**
   * Check if user can reschedule appointments
   */
  canRescheduleAppointments(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Check if user can cancel appointments
   */
  canCancelAppointments(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Check if user can change appointment/booking statuses.
   *
   * Super User and Scheduling Assistant get this implicitly via the broader
   * `Routemize.Appointment.Update` permission. Team Members get it via the
   * narrower `Routemize.Appointment.ChangeStatus` permission, which the
   * backend enforces to a status-only diff on their *own* assigned
   * appointments (see `docs/implementation/team-member-change-status-backend-spec.md`).
   */
  canChangeStatuses(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update') ||
           this.hasPermission(config, 'Routemize.Appointment.ChangeStatus');
  }

  /**
   * Check if user can create ad-hoc stops on their own schedule via
   * `POST /api/app/appointment/createStop` (Plan a Stop / Add a Stop /
   * Bulk Route ad-hoc stops).
   *
   * Super User and Scheduling Assistant get this implicitly via the broader
   * `Routemize.Appointment.Update` permission. Team Members get it via the
   * narrower `Routemize.Appointment.CreateStop` permission, which the
   * backend enforces to require `assignedUserId === currentUser.id` (Team
   * Members can only drop stops onto their own day). See
   * `docs/implementation/team-member-create-own-stop-backend-spec.md`.
   */
  canCreateOwnStop(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update') ||
           this.hasPermission(config, 'Routemize.Appointment.CreateStop');
  }

  /**
   * Check if user can hard-delete an ad-hoc stop on their own schedule
   * via `DELETE /api/app/appointment/{id}/delete`.
   *
   * Super User and Scheduling Assistant get this implicitly via the broader
   * `Routemize.Appointment.Update` permission (which authorizes delete on
   * any appointment in the tenant). Team Members get it via the narrower
   * `Routemize.Appointment.DeleteOwnStop` permission, which the backend
   * enforces to require `Source = InternalStop` AND
   * `AssignedUserId === currentUser.id` (Team Members cannot delete real
   * customer appointments or someone else's ad-hoc stops). See
   * `docs/implementation/team-member-delete-own-ad-hoc-stop-backend-spec.md`.
   *
   * NOTE: This flag does **not** by itself authorize deletion of a regular
   * customer appointment — callers must additionally check that the target
   * appointment is ad-hoc (`isAdHocStop === true`) before showing a
   * Delete affordance to a non-admin user.
   */
  canDeleteOwnAdHocStop(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update') ||
           this.hasPermission(config, 'Routemize.Appointment.DeleteOwnStop');
  }

  /**
   * Check if user can be assigned to appointments/events.
   * Role-based: Super User and Team Member can be assigned, Scheduling Assistant cannot.
   */
  canBeAssignedToEvents(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const roleType = UserRoleConfigManager.determineRoleType(config?.currentUser?.roles || []);
    return roleType !== 'booking_admin';
  }

  // ========================================
  // Booking Form Permissions
  // ========================================

  /**
   * Check if user can view booking forms
   */
  canViewBookingForms(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.BookingForm.Read') ||
           this.hasPermission(config, 'Routemize.BookingForm');
  }

  /**
   * Check if user can create booking forms
   */
  canCreateBookingForms(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.BookingForm.Create');
  }

  /**
   * Check if user can edit booking forms
   */
  canEditBookingForms(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.BookingForm.Update');
  }

  /**
   * Check if user can delete booking forms
   */
  canDeleteBookingForms(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.BookingForm.Delete');
  }

  // ========================================
  // Company Settings Permissions
  // ========================================

  /**
   * Check if user can edit company-level settings
   */
  canEditCompanySettings(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.CompanySettings.Update') ||
           this.hasPermission(config, 'Routemize.CompanySettings');
  }

  // ========================================
  // Dashboard Permissions
  // ========================================

  /**
   * Check if user can view all users' dashboard (has appointment management)
   */
  canViewAllDashboard(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Check if user is limited to their own dashboard only
   */
  isOwnDashboardOnly(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return !this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  // ========================================
  // Billing Permissions
  // ========================================

  /**
   * Check if user can view plan/billing information.
   * Role-based: only Super Users see billing UI. Scheduling Assistants and
   * Team Members are tenant users without responsibility for the subscription
   * and should not see plan, pricing, or payment information.
   */
  canViewBilling(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const roleType = UserRoleConfigManager.determineRoleType(config?.currentUser?.roles || []);
    return roleType === 'super_user';
  }

  // ========================================
  // Voice Agent Permissions
  // ========================================

  /**
   * Check if user can access the Voice Agent area (sidebar item + page).
   * Role-based: Super Users and Scheduling Assistants (office staff who
   * handle inbound booking calls) can access it; Team Members cannot.
   * Uses hasSchedulingAssistantRole rather than determineRoleType so users
   * holding both SA and Team Member roles keep access.
   */
  canAccessVoiceAgent(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    const roles = config?.currentUser?.roles || [];
    return UserRoleConfigManager.determineRoleType(roles) === 'super_user' ||
           UserRoleConfigManager.hasSchedulingAssistantRole(roles);
  }

  // ========================================
  // Role-Based Permission Helpers
  // ========================================

  /**
   * Determine user's role type from currentUser.roles
   */
  determineUserRoleType(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): UserRoleType {
    const roles = config?.currentUser?.roles || [];
    return UserRoleConfigManager.determineRoleType(roles);
  }

  /**
   * Check if user has full booking management capabilities
   * (Booking Admin or Super User)
   */
  hasBookingManagementAccess(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): boolean {
    return this.hasPermission(config, 'Routemize.Appointment.Update');
  }

  /**
   * Get a summary of user's booking-related capabilities
   */
  getBookingCapabilitiesSummary(config: Routemize_DTOs_AppConfiguration_AppConfigurationDto | null): {
    canManageAllBookings: boolean;
    canManageRequests: boolean;
    canReassignUsers: boolean;
    canReschedule: boolean;
    canCancel: boolean;
    canChangeStatuses: boolean;
    canCreateOwnStop: boolean;
    canDeleteOwnAdHocStop: boolean;
    canEditBookingForms: boolean;
    canCreateBookingForms: boolean;
    canBeAssignedToEvents: boolean;
    canEditCompanySettings: boolean;
    canViewAllDashboard: boolean;
    roleType: UserRoleType;
  } {
    return {
      canManageAllBookings: this.canManageAllBookings(config),
      canManageRequests: this.canManageRequests(config),
      canReassignUsers: this.canReassignUsers(config),
      canReschedule: this.canRescheduleAppointments(config),
      canCancel: this.canCancelAppointments(config),
      canChangeStatuses: this.canChangeStatuses(config),
      canCreateOwnStop: this.canCreateOwnStop(config),
      canDeleteOwnAdHocStop: this.canDeleteOwnAdHocStop(config),
      canEditBookingForms: this.canEditBookingForms(config),
      canCreateBookingForms: this.canCreateBookingForms(config),
      canBeAssignedToEvents: this.canBeAssignedToEvents(config),
      canEditCompanySettings: this.canEditCompanySettings(config),
      canViewAllDashboard: this.canViewAllDashboard(config),
      roleType: this.determineUserRoleType(config)
    };
  }
}

// Export singleton instance
export const permissionManager = new PermissionManager();